import { Injectable } from '@angular/core';
import { Router, ActivatedRouteSnapshot } from '@angular/router';
import { AuthService } from '@services/auth.service';
import { LoggingService } from '@services/logging.service';
import { UserService } from '@services/user.service';
import { GraphService } from '@services/graph.service';
import { DialogService } from '@services/dialog.service';
import * as Constants from '@shared/_constants';
import { PrivacyNoticeDialogComponent } from '../privacynotice/privacynotice-dialog.component';
import { WebAPIService } from '@services/webapi.service';
import { RouterLink } from '@shared/_constants';
import { NavigationService } from '@services/navigation.service';
import { LicensingCountry } from '@models/licensingCountry.model';
import { StudyService } from './study.service';
@Injectable()
export class AuthGuard {
  constructor(
    private router: Router,
    private authService: AuthService,
    private webAPIService: WebAPIService,
    private dialogService: DialogService,
    private graphService: GraphService,
    private userService: UserService,
    private logger: LoggingService,
    private navigationService: NavigationService,
    private studyService: StudyService
  ) {}

  private checkFirstTimeUser = true;

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean> {
    const param = route.params;

    this.logger.info(this.constructor.name, 'canActivate', 'Start');

    /* Azure AD flow is getting 500 on ADFS now */
    // return true;
    /* Comment everything below and just always return true if AD Auth Flow is a problem */
    let canActivate = false;

    // check Azure Token
    if (this.authService.hasValidAzureADAccessToken()) {
      canActivate = true;
    } else {
      await this.authService.logIn();
      if (this.authService.hasValidAzureADAccessToken()) {
        canActivate = true;
      }
    }

    // check Web API Token
    if (canActivate === true) {
      this.authService.tokenReceived.subscribe(() => {
        this.webAPIService.hasValidWebAPIAccessToken().then((r) => {
          if (r === false) {
            // this.logger.info(this.constructor.name, 'canActivate', 'router.navigate to nostudyaccess');
            this.router.navigate([RouterLink.NoStudyAccess]);
          }
        });
      });

      const currentUser = await this.graphService.getCurrentUser();
      const userDetails = await this.userService.EnsureUser(currentUser);

      // if they can activate and we haven't checked if it's their first time
      if (this.checkFirstTimeUser === true) {
        if (this.navigationService.userCountry === undefined) {
          this.navigationService.userCountry = new LicensingCountry();
          this.navigationService.userCountry.countryName = currentUser.country;
        }
        const isToolAccessAvailable = await this.userService.IsToolAccessAvailable(currentUser);
        if (isToolAccessAvailable) {
          const userCountryId = await this.userService.getCountryId(currentUser);
          if (
            userCountryId &&
            userCountryId !== null &&
            this.userService.allCountryList !== undefined &&
            this.userService.allCountryList.length > 0
          ) {
            this.navigationService.userCountry = this.userService.allCountryList.find((z) => z.countryId === userCountryId);
          }
          if (userDetails) {
            // this.logger.info(this.constructor.name, 'canActivate', `userDetails.isFirstTimeUser=[${userDetails.isFirstTimeUser}]`);
            if (userDetails.isFirstTimeUser) {
              const confirmationResult = await this.dialogService.launchPrivacyNotice(PrivacyNoticeDialogComponent);
              if (confirmationResult === Constants.ConfirmationResult.yes) {
                userDetails.isFirstTimeUser = false;
                await this.userService.UpdateUser(userDetails);
                this.checkFirstTimeUser = false;

                // Bug 37469: Authentication - Duplicate users accounts being created
                // Task 63745: On first login, getting redirected to "No Study Access"
                // This is a bit of a hack, we will "force" redirect to My Studies for first time users
                this.router.navigate([RouterLink.MyStudies]);
              } else {
                // if they say no...we won't let them in
                // this.logger.info(this.constructor.name, 'canActivate', 'router.navigate to cancelprivacynotice');
                this.router.navigate([RouterLink.CancelPrivacyNotice]);
              }
            } else {
              // if they've checked the box before there is no need to do this stuff again
              this.checkFirstTimeUser = false;
            }
          }
        } else {
          // this.authService.logOut(Constants.LogoutReasonCode.userLoggedOut);
          this.router.navigate([RouterLink.NoStudyAccess]);
          canActivate = false;
        }
      }

      if (userDetails) {
        //whether confidential study is accessible to logged-in user
        if (param && param['id']) {
          const isConfidentialStudy = await this.studyService.IsConfidentialStudyAccessible(userDetails.userId, param['id']);
          if (!isConfidentialStudy) {
            this.router.navigate([RouterLink.NoStudyAccess]);
            canActivate = false;
          }
        }
      }
    }

    // this.logger.info(this.constructor.name, 'canActivate', `End: canActivate=[${canActivate}]`);
    return canActivate;
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot): Promise<boolean> {
    const cRoute = childRoute;
    return this.canActivate(cRoute);
  }
}
